import type { SortingFn } from "@tanstack/react-table";

import {
  FORMAT_DATE_TO_DAYJS,
  TIME_FORMAT_DAYJS,
  type Timezone
} from "@/constants/dateTime";
import {
  AMPM_REGEX,
  DAY_REGEX,
  MONTH_REGEX,
  SECOND_REGEX,
  TIME_REGEX,
  YEAR_REGEX
} from "@/constants/regex";

import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

import { DataSchemaType, DatasetDataType } from "../types/dataset";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);
export { DataSchemaType } from "../types/dataset";

// Pre-compile regex patterns to avoid recompilation on each call
const ISO8601_REGEX = /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}:\d{2}/;
const DATE_ONLY_REGEX = /^\d{4}-\d{2}-\d{2}$/;
const DATETIME_NO_TZ_REGEX = /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}:\d{2}$/;
const HAS_TIMEZONE_REGEX = /([Zz]|[+-]\d{2}:\d{2})$/;
const HAS_SECONDS_REGEX = /:\d{2}:\d{2}/;

// Regex for Go-style datetime format: "2026-01-20 00:00:00 +0000 UTC"
const GO_DATETIME_REGEX =
  /^(\d{4}-\d{2}-\d{2})\s(\d{2}:\d{2}:\d{2})\s([+-]\d{2})(\d{2})\s\w+$/;

// Pre-compiled regex patterns for time format detection (used by convertTimeToTimezone)
const TIME_24H_NO_SEC_REGEX = /^\d{2}:\d{2}$/; // "14:30"
const TIME_24H_WITH_SEC_REGEX = /^\d{2}:\d{2}:\d{2}$/; // "14:30:45"
const TIME_12H_NO_SEC_REGEX = /^\d{1,2}:\d{2}\s*[AP]M$/i; // "2:30 PM" or "02:30PM"
const TIME_12H_WITH_SEC_REGEX = /^\d{1,2}:\d{2}:\d{2}\s*[AP]M$/i; // "2:30:45 PM" or "02:30:45PM"

// Module-level cache for time conversions with size limit to prevent memory leaks
const timeConversionCache = new Map<string, string>();
const TIME_CACHE_MAX_SIZE = 1000;

function normalizeGoDatetime(date: string): string {
  const match = GO_DATETIME_REGEX.exec(date);
  if (match) {
    // Convert "YYYY-MM-DD HH:mm:ss +HHMM TZ" → "YYYY-MM-DDTHH:mm:ss+HH:MM"
    return `${match[1]}T${match[2]}${match[3]}:${match[4]}`;
  }
  return date;
}

function getCachedTimeConversion(key: string): string | undefined {
  return timeConversionCache.get(key);
}

function setCachedTimeConversion(key: string, value: string): void {
  // Evict oldest entry if cache is full (FIFO eviction)
  if (timeConversionCache.size >= TIME_CACHE_MAX_SIZE) {
    const firstKey = timeConversionCache.keys().next().value;
    if (firstKey) timeConversionCache.delete(firstKey);
  }
  timeConversionCache.set(key, value);
}

/**
 * Detects the time format of a string using fast regex matching.
 * This is much faster than trial-and-error parsing with dayjs.
 * @param timeString - The time string to detect format for
 * @returns The dayjs format string if detected, null otherwise
 */
function detectTimeFormat(timeString: string): string | null {
  if (TIME_24H_NO_SEC_REGEX.test(timeString)) return "HH:mm";
  if (TIME_24H_WITH_SEC_REGEX.test(timeString)) return "HH:mm:ss";
  if (TIME_12H_NO_SEC_REGEX.test(timeString)) return "hh:mm A";
  if (TIME_12H_WITH_SEC_REGEX.test(timeString)) return "hh:mm:ss A";
  return null;
}

// Array of possible date formats - ordered by likelihood
const dateFormats = [
  "YYYY-MM-DDTHH:mm:ss.SSSZ", // ISO with milliseconds and timezone
  "YYYY-MM-DDTHH:mm:ssZ", // ISO without milliseconds
  "YYYY-MM-DD HH:mm:ss",
  "YYYY-MM-DD",
  "DD-MM-YYYY HH:mm",
  "MM/DD/YYYY",
  "MM/DD/YYYY hh:mm A",
  "DD MMMM YYYY"
];
/**
 * Parses a date string using dayjs with multiple format fallbacks.
 *
 * This function attempts to parse the input string in multiple ways:
 * 1. First tries dayjs's native ISO 8601 parsing
 * 2. If that fails, tries each format in the predefined dateFormats array
 *
 * @param dateString - The date string to parse (can be any type, but only strings are processed)
 * @returns A dayjs.Dayjs object in UTC if parsing succeeds, null otherwise
 */
export function parseWithDayjs(
  dateString: string | unknown
): dayjs.Dayjs | null {
  if (typeof dateString !== "string" || !dateString) {
    return null;
  }

  // Quick checks for common formats before trying dayjs
  // ISO 8601 format (most common for APIs)
  if (ISO8601_REGEX.test(dateString)) {
    const parsed = dayjs.utc(dateString);
    if (parsed.isValid()) return parsed;
  }

  // Date only format
  if (DATE_ONLY_REGEX.test(dateString)) {
    const parsed = dayjs.utc(dateString, "YYYY-MM-DD", true);
    if (parsed.isValid()) return parsed;
  }

  // Datetime without timezone
  if (DATETIME_NO_TZ_REGEX.test(dateString)) {
    const parsed = dayjs.utc(dateString, "YYYY-MM-DD HH:mm:ss", true);
    if (parsed.isValid()) return parsed;
  }

  // Fallback: try remaining formats (skip first 3 which are handled by quick checks above)
  for (let i = 3; i < dateFormats.length; i++) {
    const date = dayjs.utc(dateString, dateFormats[i], true);
    if (date.isValid()) return date;
  }

  // Handle time-only formats (e.g., "09:00 AM", "14:30:00")
  const timeFormat = detectTimeFormat(dateString);
  if (timeFormat) {
    // Use a reference date (1970-01-01) for time-only parsing
    // This allows time values to be compared/sorted
    const parsed = dayjs.utc(
      `1970-01-01 ${dateString}`,
      `YYYY-MM-DD ${timeFormat}`,
      true
    );
    if (parsed.isValid()) return parsed;
  }

  return null;
}

/**
 * Checks if a date string contains seconds
 * @param dateString - The date/time string to check
 * @returns true if the string contains seconds (HH:MM:SS format)
 */
export const hasSeconds = (dateString: string): boolean => {
  // Check if the time part has seconds (HH:MM:SS vs HH:MM)
  return HAS_SECONDS_REGEX.test(dateString);
};

/**
 * Checks if a date string contains timezone information
 * @param dateString - The date/time string to check
 * @returns true if the string contains timezone info (Z suffix or +/- offset)
 */
const hasTimezoneInfo = (dateString: string): boolean => {
  // Match Z suffix (case-insensitive) or +/-HH:mm offset at the end
  return (
    /Z$/i.test(dateString) || /[+-]\d{2}:\d{2}$|[+-]\d{4}$/.test(dateString)
  );
};

/**
 *
 * @param format - "datetime" | "date" | "time". Default is "datetime".
 * @param timeZone - IANA timezone string (e.g., "Asia/Jakarta"). Default is "UTC".
 * @returns Formatted string or original input if invalid.
 *
 */
export function formatDate(
  date: string,
  format: DataSchemaType.DATE | DataSchemaType.DATE_TIME | DataSchemaType.TIME,
  timeZone?: { code: string; name: string }
): string {
  if (typeof date !== "string" || !date.trim()) return "";

  // Handle time-only strings when format is TIME
  if (format === DataSchemaType.TIME) {
    const parsed = dayjs.utc(`1970-01-01T${date}`);
    const timezone = parsed.tz(timeZone?.code);
    // Preserve seconds if present in input
    return timezone.format(hasSeconds(date) ? "HH:mm:ss" : "HH:mm");
  }

  // Normalize Go-style datetime format (e.g., "2026-01-20 00:00:00 +0000 UTC")
  let normalizedDate = date;
  if (GO_DATETIME_REGEX.test(date)) {
    normalizedDate = normalizeGoDatetime(date);
  }

  // If input has no timezone info, return as-is (no conversion)
  // But first validate that it's a parseable date using our multi-format parser
  if (!hasTimezoneInfo(normalizedDate)) {
    const parsed = parseWithDayjs(normalizedDate);
    if (!parsed) {
      return "Invalid Date";
    }
    return date;
  }

  const parsed = dayjs.utc(normalizedDate);
  const timezone = parsed.tz(timeZone?.code);

  if (format === DataSchemaType.DATE) {
    return timezone.format("YYYY-MM-DD");
  } else if (format === DataSchemaType.DATE_TIME) {
    return timezone.format("YYYY-MM-DD HH:mm:ss");
  }

  return date;
}

export function formatToTimezone(
  date: string,
  timeZone?: { code: string; name: string }
) {
  if (typeof date !== "string" || !date.trim()) return "";
  if (timeZone) {
    const parsed = dayjs.utc(date);
    const timezone = parsed.tz(timeZone?.code);
    return timezone.format();
  }
  return date;
}

/**
 * Special utils to convert time_format from Go backend to Day.js format in Dataset Detail
 * @param date string date
 * @param goFormat - Format string from backend (Go)
 * @param data_type - Optional data type to handle default formats
 * @returns Converted Day.js format
 */
export function convertFormatterDayjs(
  date: string,
  goFormat: string,
  data_type?: DataSchemaType,
  fromFormat?: string | null
): string {
  let format: string;

  if (!goFormat && data_type === DataSchemaType.DATE) format = "YYYY-MM-DD";
  else if (!goFormat && data_type === DataSchemaType.DATE_TIME) format = "";
  else if (!goFormat && data_type === DataSchemaType.TIME) return date;
  else {
    const mapped = FORMAT_DATE_TO_DAYJS[goFormat];
    format =
      mapped ||
      goFormat
        .replace(TIME_REGEX, "HH:mm")
        .replace(SECOND_REGEX, ":ss")
        .replace(AMPM_REGEX, "A")
        .replace(MONTH_REGEX, "MMMM")
        .replace(YEAR_REGEX, "YYYY")
        .replace(DAY_REGEX, "DD");
  }

  // Handle time only format
  if (TIME_FORMAT_DAYJS.includes(format)) {
    const parsedTime = dayjs(date, TIME_FORMAT_DAYJS);
    return parsedTime.format(format);
  }
  const mappedFromFormat = FORMAT_DATE_TO_DAYJS[fromFormat || ""];

  return dayjs.utc(date, mappedFromFormat).format(format);
}

/**
 * Converts time string from UTC to user timezone and normalizes to 24-hour format
 * Handles various input formats including 12-hour AM/PM and times with timezone offsets
 * @param timeString - Time string in various formats (assumed UTC unless timezone info present)
 * @param timezone - User timezone to convert to
 * @returns Converted and normalized time string in 24-hour format
 */
export function convertTimeToTimezone(
  timeString: string,
  timezone?: Timezone,
  referenceDate?: string
): string {
  if (!timeString || typeof timeString !== "string" || !timeString.trim()) {
    return "";
  }

  const trimmedTime = timeString.trim();

  // Check cache first
  const cacheKey = `${trimmedTime}|${timezone?.code ?? ""}|${referenceDate ?? ""}`;
  const cached = getCachedTimeConversion(cacheKey);
  if (cached !== undefined) {
    return cached;
  }

  // Handle Go-style datetime format (e.g., "0000-01-01 17:00:00 +0000 UTC")
  if (GO_DATETIME_REGEX.test(trimmedTime)) {
    const normalized = normalizeGoDatetime(trimmedTime);
    const parsed = dayjs.utc(normalized);

    if (parsed.isValid()) {
      const outputFormat = "HH:mm";

      let result: string;
      if (timezone?.code) {
        // Use reference date to get correct timezone offset (for DST handling)
        // Extract just the time, apply it to a real date, then convert
        const time = parsed.format("HH:mm:ss");
        const baseDate = referenceDate || dayjs().format("YYYY-MM-DD");
        const fullDatetime = dayjs.utc(`${baseDate}T${time}`);
        result = fullDatetime.tz(timezone.code).format(outputFormat);
      } else {
        result = parsed.format(outputFormat);
      }
      setCachedTimeConversion(cacheKey, result);
      return result;
    }
  }

  // Check if time already has timezone information (e.g., "14:30Z", "14:30+07:00", "14:30-05:00")
  const hasTimezoneInfo = HAS_TIMEZONE_REGEX.test(trimmedTime);

  if (hasTimezoneInfo) {
    // Parse time with timezone info - dayjs will handle the conversion
    // We need to create a full datetime to parse timezone-aware time
    // Use provided reference date or default to current date to handle DST correctly
    const baseDate = `${referenceDate || dayjs().format("YYYY-MM-DD")}T`;
    const isoTime = trimmedTime.replace(
      /^(\d{2}:\d{2}(:\d{2})?)/,
      `${baseDate}$1`
    );
    const parsed = dayjs(isoTime);

    if (parsed.isValid()) {
      const hasSecondsInInput = HAS_SECONDS_REGEX.test(trimmedTime);
      const outputFormat = hasSecondsInInput ? "HH:mm:ss" : "HH:mm";

      let result: string;
      if (timezone?.code) {
        result = parsed.tz(timezone.code).format(outputFormat);
      } else {
        result = parsed.utc().format(outputFormat);
      }
      setCachedTimeConversion(cacheKey, result);
      return result;
    }
  }

  // Detect format using fast regex matching instead of trial-and-error parsing
  const detectedFormat = detectTimeFormat(trimmedTime);

  if (detectedFormat) {
    const parsed = dayjs.utc(trimmedTime, detectedFormat, true); // strict parsing, treat as UTC

    if (parsed.isValid()) {
      // Check if input format has AM/PM - preserve the format style
      const hasAmPm = detectedFormat.includes(" A");
      const hasSecondsInFormat = detectedFormat.includes(":ss");

      // Determine output format based on input format style and whether seconds are present
      // If input has AM/PM, output should also have AM/PM
      let outputFormat: string;
      if (hasAmPm) {
        outputFormat = hasSecondsInFormat ? "hh:mm:ss A" : "hh:mm A";
      } else {
        outputFormat = hasSecondsInFormat ? "HH:mm:ss" : "HH:mm";
      }

      // Convert to user timezone if provided, otherwise return UTC
      let result: string;
      if (timezone?.code) {
        result = parsed.tz(timezone.code).format(outputFormat);
      } else {
        result = parsed.format(outputFormat);
      }
      setCachedTimeConversion(cacheKey, result);
      return result;
    }
  }

  // If no format matched, cache and return original value
  setCachedTimeConversion(cacheKey, trimmedTime);
  return trimmedTime;
}

/**
 * Translates datetime value to user timezone based on DatasetDataType
 * @param timezone - User timezone from profile { code: string, name: string }
 * @param datetype - DatasetDataType enum value
 * @param value - The datetime string value to convert (assumed UTC)
 * @returns Formatted string in user's timezone
 */
export function translateToUserTimezone(
  timezone: Timezone | undefined,
  datetype: DatasetDataType,
  value: string
): string {
  if (!value || typeof value !== "string" || !value.trim()) return "";

  // Handle time type separately - needs custom parsing and timezone conversion
  if (datetype === DatasetDataType.TYPE_TIME) {
    return convertTimeToTimezone(value, timezone);
  }

  if (datetype === DatasetDataType.TYPE_DATETIME) {
    return formatDate(value, DataSchemaType.DATE_TIME, timezone);
  }

  if (datetype === DatasetDataType.TYPE_DATE) {
    return formatDate(value, DataSchemaType.DATE, timezone);
  }

  // Return value unchanged for non-datetime types
  return value;
}

export const isDatetime = (data_type: DataSchemaType): boolean => {
  return (
    data_type === DataSchemaType.DATE ||
    data_type === DataSchemaType.TIME ||
    data_type === DataSchemaType.DATE_TIME
  );
};

/**
 * Creates a TanStack Table sorting function that properly handles date/time values
 * including AM/PM format, 24-hour format, ISO datetime, and date-only formats.
 *
 * Unlike the built-in "datetime" sortingFn which uses `new Date(value).getTime()`
 * and fails for time strings with AM/PM format, this function uses parseWithDayjs
 * which handles multiple date/time formats correctly.
 *
 * @returns A SortingFn compatible with TanStack Table's sortingFn option
 *
 * @example
 * // In your column definition:
 * {
 *   accessorKey: "time",
 *   header: "Time",
 *   sortingFn: createDateSortingFn()
 * }
 */
export function createDateSortingFn<T>(): SortingFn<T> {
  return (rowA, rowB, columnId) => {
    const dateA = parseWithDayjs(rowA.getValue(columnId));
    const dateB = parseWithDayjs(rowB.getValue(columnId));

    if (!dateA && !dateB) return 0;
    if (!dateA) return 1;
    if (!dateB) return -1;

    return dateA.diff(dateB);
  };
}
